import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import type { DxcChartTone } from './dxc-donut.component';

/** Una cubeta de la gráfica: su rótulo y un valor por serie, en el orden de `series`. */
export type DxcBarPoint = { label: string; values: number[] };

const COUNT = new Intl.NumberFormat('es-MX');
const NICE_STEPS = [1, 2, 3, 4, 5, 6, 8, 10];

/**
 * Barras dobles: dos series por cubeta sobre el mismo eje. Es la gráfica de
 * actividad del dashboard. Se dibuja con CSS (`.dxc-bars`); no hay librería de
 * gráficas en el producto y no hace falta para esto.
 *
 * El eje se redondea a una cifra «limpia» (`scaleMax`) para que las marcas
 * —máximo, mitad y cero— sean enteros que se leen, no el máximo crudo del
 * periodo. El detalle de cada cubeta sale al apuntarla: las barras solas no
 * dicen la cifra.
 */
@Component({
    standalone: true,
    selector: 'dxc-bar-chart',
    template: `
        <div class="dxc-bars" [class.dxc-bars--empty]="isEmpty">
            <div class="dxc-bars__legend">
                @for (name of series; track name; let s = $index) {
                    <span class="dxc-bars__key">
                        <span class="dxc-bars__swatch dxc-tone--{{ toneOf(s) }}"></span>{{ name }}
                    </span>
                }
            </div>
            <div class="dxc-bars__frame">
                <div class="dxc-bars__axis" aria-hidden="true">
                    @for (tick of ticks; track $index) { <span>{{ tick }}</span> }
                </div>
                <div class="dxc-bars__plot" role="img" [attr.aria-label]="ariaLabel">
                    @if (isEmpty) { <div class="dxc-bars__empty">{{ emptyText }}</div> }
                    @for (point of points; track $index; let i = $index) {
                        <div class="dxc-bars__col" [style.--dxc-bar-i]="i">
                            @for (value of point.values; track $index; let s = $index) {
                                <div class="dxc-bars__bar dxc-tone--{{ toneOf(s) }}" [style.height.%]="heightOf(value)"></div>
                            }
                            <div class="dxc-bars__tip" aria-hidden="true">
                                <span class="dxc-bars__tip-title">{{ point.label }}</span>
                                @for (value of point.values; track $index; let s = $index) {
                                    <span class="dxc-bars__tip-row">
                                        <span class="dxc-bars__swatch dxc-tone--{{ toneOf(s) }}"></span>
                                        <span>{{ series[s] }}</span>
                                        <b>{{ format(value) }}</b>
                                    </span>
                                }
                            </div>
                        </div>
                    }
                </div>
                <div class="dxc-bars__labels" aria-hidden="true">
                    @for (point of points; track $index; let i = $index) {
                        <span class="dxc-bars__label">{{ points.length > 8 && i % 2 === 1 ? '' : point.label }}</span>
                    }
                </div>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcBarChartComponent {

    /** Nombre de cada serie, para la leyenda y el detalle de cada cubeta. Máximo dos. */
    @Input() series: string[] = [];
    @Input() points: DxcBarPoint[] = [];
    /**
     * Tono de cada serie, en el orden de `series`. Dos tonos distintos y no el
     * mismo en claro y en lleno: así cada serie puede llevar el color de lo
     * que cuenta (lo timbrado en `success`, lo asignado en `accent`).
     */
    @Input() tones: DxcChartTone[] = ['info', 'accent'];
    @Input() emptyText = 'Sin actividad en este periodo';
    @Input() ariaLabel = '';

    get isEmpty(): boolean {
        return this.points.every(point => point.values.every(value => !value));
    }

    private get max(): number {
        return Math.max(1, ...this.points.flatMap(point => point.values));
    }

    /**
     * Techo del eje: el doble de un paso «limpio» que cubre la mitad del
     * máximo. Así la marca del medio es un entero y la de arriba nunca queda
     * por debajo de la barra más alta. Los pasos son finos a propósito —con
     * solo 1, 2, 5 y 10, un máximo de 11 subía el techo a 20 y la barra más
     * alta se quedaba a media gráfica—.
     */
    get scaleMax(): number {
        const half = this.max / 2;
        const magnitude = Math.pow(10, Math.floor(Math.log10(half)));
        const factor = half / magnitude;
        const nice = NICE_STEPS.find(step => step >= factor) ?? 10;
        return Math.max(1, Math.round(nice * magnitude)) * 2;
    }

    /** De arriba abajo: máximo, mitad, cero. */
    get ticks(): string[] {
        const top = this.scaleMax;
        return [COUNT.format(top), COUNT.format(top / 2), '0'];
    }

    toneOf(index: number): DxcChartTone {
        return this.tones[index] ?? 'accent';
    }

    format(value: number): string {
        return COUNT.format(value ?? 0);
    }

    /** Una barra con valor nunca baja de un 3 %: si no, un 1 junto a un 400 desaparece. */
    heightOf(value: number): number {
        if (!value) return 0;
        return Math.max(3, (value / this.scaleMax) * 100);
    }
}
